import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import DataTable from "@/components/DataTable";
import { Wrench } from "lucide-react";

const STATUS_COLORS = {
  Pending: "bg-slate-100 text-slate-700 border-slate-200",
  Ongoing: "bg-amber-50 text-amber-800 border-amber-200",
  "Revision Required": "bg-rose-50 text-rose-700 border-rose-200",
  Released: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
};

function EngBadge({ status }) {
  const cls = STATUS_COLORS[status] || STATUS_COLORS.Pending;
  return (
    <span data-testid={`eng-badge-${status}`}
          className={`inline-flex items-center gap-1.5 border px-2 py-0.5 rounded text-[11px] font-semibold uppercase font-mono ${cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {status}
    </span>
  );
}

export default function DesignQueue() {
  const nav = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));
    api.get("/engineering/stats").then((r) => setStats(r.data || null)).catch(() => setStats(null));
  }, []);

  const fetchData = React.useCallback(async ({ skip, limit, sort, search, filters }) => {
    const params = { skip, limit, sort, search };
    if (filters.engineering_status) params.status = filters.engineering_status;
    if (filters.customer_id) params.customer_id = filters.customer_id;
    try {
      const { data } = await api.get("/engineering/queue", { params });
      return data || { items: [], total: 0 };
    } catch (err) {
      // Empty database or transient failure — degrade gracefully.
      // eslint-disable-next-line no-console
      console.warn("[DesignQueue] /engineering/queue failed:", err?.response?.status, err?.message);
      return { items: [], total: 0 };
    }
  }, []);

  const columns = [
    { key: "cylinder_number", label: "Cyl #", sortable: true,
      render: (r) => <span className="font-mono text-[13px] font-bold text-blue-900">#{r.cylinder_number}</span> },
    { key: "customer_name", label: "Customer", className: "text-slate-800" },
    { key: "plant_name", label: "Plant", visibleByDefault: false, className: "text-slate-600 text-xs" },
    { key: "oa_number", label: "OA #",
      render: (r) => <span className="font-mono text-xs text-slate-700">{r.oa_number || "—"}</span> },
    { key: "drawing_number", label: "Drawing #",
      render: (r) => <span className="font-mono text-xs text-slate-700">{r.drawing_number || <span className="text-slate-300">—</span>}</span> },
    { key: "current_revision", label: "Rev",
      render: (r) => <span className="font-mono text-xs font-semibold">{r.current_revision || <span className="text-slate-300">—</span>}</span> },
    { key: "design_engineer_name", label: "Design Engineer",
      render: (r) => <span className="text-xs text-slate-700">{r.design_engineer_name || <span className="text-slate-300 italic">Unassigned</span>}</span> },
    { key: "engineering_status", label: "Status", sortable: true,
      render: (r) => <EngBadge status={r.engineering_status} /> },
    { key: "assigned_date", label: "Assigned",
      render: (r) => <span className="font-mono text-xs text-slate-600">{r.assigned_date ? r.assigned_date.slice(0, 10) : "—"}</span> },
    { key: "released_date", label: "Released", visibleByDefault: false,
      render: (r) => <span className="font-mono text-xs text-slate-600">{r.released_date || "—"}</span> },
    { key: "design_lead_time_days", label: "Lead Time",
      render: (r) => r.design_lead_time_days != null
        ? <span className={`font-mono text-xs font-bold ${r.is_released ? "text-emerald-700" : "text-amber-700"}`}>{r.design_lead_time_days}d</span>
        : <span className="text-slate-300">—</span> },
  ];

  return (
    <div data-testid="design-queue-page" className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">Engineering · Module 4</div>
        <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo, sans-serif" }}>Design Work Queue</h1>
        <p className="text-sm text-slate-500 mt-1">
          Every cylinder must have its engineering released before production can begin.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { label: "Pending", value: stats.pending, cls: "text-slate-700" },
            { label: "Ongoing", value: stats.ongoing, cls: "text-amber-700" },
            { label: "Revision Required", value: stats.revision_required, cls: "text-rose-700" },
            { label: "Released", value: stats.released, cls: "text-emerald-700" },
            { label: "Rejected", value: stats.rejected, cls: "text-red-700" },
            { label: "Avg Lead Time", value: stats.avg_lead_time_days != null ? `${stats.avg_lead_time_days}d` : "—", cls: "text-blue-900" },
          ].map((k) => (
            <div key={k.label} data-testid={`eng-kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}
                 className="bg-white border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k.label}</div>
              <div className={`mt-1 font-mono text-2xl font-bold ${k.cls}`}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      <DataTable
        columns={columns}
        fetchData={fetchData}
        filters={[
          { key: "engineering_status", label: "Status",
            options: ["Pending", "Ongoing", "Revision Required", "Released", "Rejected"].map((s) => ({ value: s, label: s })) },
          { key: "customer_id", label: "Customer",
            options: customers.map((c) => ({ value: c.id, label: c.name })) },
        ]}
        storageKey="design-queue"
        onRowClick={(r) => nav(`/engineering/${r.id}`)}
        emptyMessage="No cylinders in the design queue yet. Release an Order Acceptance to Production first."
      />
    </div>
  );
}
